import { ChevronLeft, ChevronRight } from '../../../components/icon/solar-icons.js'
import { useEffect, useState } from 'react'

import { navigateTo } from '../../../../common/frontend/route-utils.js'
import { t } from '../../../../common/i18n.js'
import { Language } from '../../../../common/types/i18n.js'
import { PlaytimeCampaignWithQuestions } from '../../../../common/types/playtime.js'
import { Button } from '../../../components/button/button.js'
import { PageHeader } from '../../../components/page-header/page-header.js'
import { PageShell } from '../../../components/page-shell/page-shell.js'
import { Spinner } from '../../../components/spinner/spinner.js'
import { SurveyQuestion } from '../../../components/survey-question/survey-question.js'
import { ToggleGroup } from '../../../components/toggle-group/toggle-group.js'
import { View } from '../../../types.js'
import { handleError } from '../../../utils/error-utils.js'
import { refreshActiveCampaign } from '../../../utils/load-utils.js'
import { ViewBase, ViewBaseProps } from '../../base/base.js'
import './survey.scss'

export interface SurveyViewProps {
    base: ViewBaseProps
    campaign: PlaytimeCampaignWithQuestions | null
    isLoading: boolean
    view: View
    /**
     * Admin preview (TASK-313): no status check, a display-mode switch, and
     * "submit" only shows the thank-you screen — nothing is sent.
     */
    preview?: boolean
}

type SurveyStep =
    | 'intro'
    | 'questions'
    | 'confirm'
    | 'already-answered'
    | 'closed'
    | 'not-open'
    | 'no-group'

export const SurveyView = (props: SurveyViewProps) => {
    const [step, setStep] = useState<SurveyStep>('intro')
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
    const [answers, setAnswers] = useState<Record<string, string>>({})
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [statusChecked, setStatusChecked] = useState(props.preview === true)
    const [previewDisplayMode, setPreviewDisplayMode] = useState<
        'one_at_a_time' | 'all_at_once' | null
    >(null)
    // The respondent picks the survey language (questions, tooltips, scale
    // labels, buttons); it opens in English by default (the questionnaire's
    // source language) and stays local to the survey so the rest of the Hub
    // is untouched. The toggle lives on the questions step only (TASK-313):
    // the intro/outro texts are written in one language by the admin, so a
    // toggle on the welcome page would switch the chrome but not the text.
    const [surveyLang, setSurveyLang] = useState<Language>('en')

    const { campaign, view, isLoading, preview = false } = props
    const lang = surveyLang
    const previewStrip = preview ? (
        <div className="survey__preview" role="note">
            <span className="survey__preview-pill">{t.playtime.q12.previewLabel(lang)}</span>
            <span className="survey__preview-note">{t.playtime.q12.previewNote(lang)}</span>
            <ToggleGroup<'one_at_a_time' | 'all_at_once'>
                ariaLabel={t.playtime.q12.previewDisplayMode(lang)}
                value={previewDisplayMode ?? campaign?.display_mode ?? 'one_at_a_time'}
                onChange={setPreviewDisplayMode}
                options={[
                    { value: 'one_at_a_time', label: t.playtime.q12.previewOneAtATime(lang) },
                    { value: 'all_at_once', label: t.playtime.q12.previewAllAtOnce(lang) },
                ]}
            />
        </div>
    ) : null
    const languageToggle = (
        <div className="survey__lang">
            <span className="survey__lang-label">{t.playtime.q12.answerLanguage(lang)}</span>
            <ToggleGroup<Language>
                ariaLabel={t.playtime.q12.answerLanguage(lang)}
                value={lang}
                onChange={setSurveyLang}
                options={[
                    { value: 'et', label: 'ET' },
                    { value: 'en', label: 'EN' },
                ]}
            />
        </div>
    )

    // Check if already answered, campaign is closed, or user has no group
    useEffect(() => {
        if (!campaign || preview) return

        const checkStatus = async () => {
            try {
                // The server decides open/closed on the Estonian date (inclusive
                // start/end) and whether this user has already answered.
                const statusRes = await view.api.playtime.getCampaignStatus(campaign.id)
                if (statusRes.data.state === 'closed') {
                    setStep('closed')
                    setStatusChecked(true)
                    return
                }
                if (statusRes.data.state === 'not-open') {
                    setStep('not-open')
                    setStatusChecked(true)
                    return
                }
                if (statusRes.data.answered) {
                    setStep('already-answered')
                    setStatusChecked(true)
                    return
                }

                // Group membership will be checked at submit time
                // If the user is not in a group, the 403 error is caught in handleSubmit
            } catch {
                // Non-critical — proceed to survey
            } finally {
                setStatusChecked(true)
            }
        }

        void checkStatus()
    }, [campaign, view, preview])

    if (isLoading || !campaign || !statusChecked) {
        return (
            <ViewBase {...props.base}>
                <Spinner />
            </ViewBase>
        )
    }

    const questions = [...campaign.questions].sort((a, b) => a.position - b.position)
    const currentQuestion = questions[currentQuestionIndex]
    const totalQuestions = questions.length
    const answeredCount = Object.keys(answers).filter(
        (k) => answers[k] && answers[k].trim() !== '',
    ).length
    const allAnswered = questions
        .filter((q) => q.is_required)
        .every((q) => answers[String(q.id)] && answers[String(q.id)].trim() !== '')

    // Format deadline date
    const deadlineDate = new Date(campaign.end)
    const formattedDeadline = deadlineDate.toLocaleDateString(lang === 'et' ? 'et-EE' : 'en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    })

    const handleAnswer = (questionId: number, value: string) => {
        setAnswers((prev) => ({ ...prev, [String(questionId)]: value }))
    }

    const handlePrevQuestion = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(currentQuestionIndex - 1)
        }
    }

    const handleNextQuestion = () => {
        if (currentQuestionIndex < totalQuestions - 1) {
            setCurrentQuestionIndex(currentQuestionIndex + 1)
        }
    }

    const handleSubmit = async () => {
        if (preview) {
            setStep('confirm')
            return
        }
        setIsSubmitting(true)
        try {
            await view.api.playtime.submitSurvey(campaign.id, answers)
            setStep('confirm')
            // Flip the dashboard banner / sidebar button to the answered state.
            void refreshActiveCampaign(view)
        } catch (err) {
            // If 403 — user not in group (or the campaign is no longer open)
            if (err instanceof Error && err.message.includes('403')) {
                setStep('no-group')
            } else {
                handleError(view, err)
            }
        } finally {
            setIsSubmitting(false)
        }
    }

    // Already answered state
    if (step === 'already-answered') {
        return (
            <ViewBase {...props.base}>
                <PageShell>
                    <div className="survey">
                        <div className="survey__status">
                            <h1 className="survey__title">
                                {t.playtime.q12.alreadyAnsweredTitle(lang)}
                            </h1>
                            <p className="survey__description">
                                {t.playtime.q12.alreadyAnsweredDescription(lang)}
                            </p>
                            <Button
                                text={t.playtime.q12.backToHome(lang)}
                                variant="primary"
                                onClick={() => {
                                    navigateTo('/portal')
                                }}
                            />
                        </div>
                    </div>
                </PageShell>
            </ViewBase>
        )
    }

    // Campaign closed state
    if (step === 'closed' || step === 'not-open') {
        const startDate = new Date(campaign.start)
        const formattedStart = startDate.toLocaleDateString(lang === 'et' ? 'et-EE' : 'en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        })
        return (
            <ViewBase {...props.base}>
                <PageShell>
                    <div className="survey">
                        <div className="survey__status">
                            <h1 className="survey__title">
                                {step === 'closed'
                                    ? t.playtime.q12.closedTitle(lang)
                                    : t.playtime.q12.notOpenTitle(lang)}
                            </h1>
                            <p className="survey__description">
                                {step === 'closed'
                                    ? t.playtime.q12.closedDescription(formattedDeadline)(lang)
                                    : t.playtime.q12.notOpenDescription(formattedStart)(lang)}
                            </p>
                            <Button
                                text={t.playtime.q12.backToHome(lang)}
                                variant="primary"
                                onClick={() => {
                                    navigateTo('/portal')
                                }}
                            />
                        </div>
                    </div>
                </PageShell>
            </ViewBase>
        )
    }

    // Not in any group state (BUG-007 fix)
    if (step === 'no-group') {
        return (
            <ViewBase {...props.base}>
                <PageShell>
                    <div className="survey">
                        <div className="survey__status">
                            <h1 className="survey__title">{campaign.name}</h1>
                            <p className="survey__description">
                                {t.playtime.q12.notInGroup(lang)}
                            </p>
                            <Button
                                text={t.playtime.q12.backToHome(lang)}
                                variant="primary"
                                onClick={() => {
                                    navigateTo('/portal')
                                }}
                            />
                        </div>
                    </div>
                </PageShell>
            </ViewBase>
        )
    }

    // Intro step
    if (step === 'intro') {
        return (
            <ViewBase {...props.base}>
                <PageShell>
                    <div className="survey">
                        {previewStrip}
                        <div className="survey__intro">
                            <h1 className="survey__title">{campaign.name}</h1>
                            {campaign.intro_description && (
                                <div
                                    className="survey__description survey__description--body"
                                    dangerouslySetInnerHTML={{
                                        __html: campaign.intro_description,
                                    }}
                                />
                            )}
                            <div className="survey__deadline">
                                {t.playtime.q12.deadline(lang)}: {formattedDeadline}
                            </div>
                            <Button
                                text={t.playtime.q12.startSurvey(lang)}
                                variant="primary"
                                onClick={() => setStep('questions')}
                            />
                        </div>
                    </div>
                </PageShell>
            </ViewBase>
        )
    }

    // Confirm step
    if (step === 'confirm') {
        return (
            <ViewBase {...props.base}>
                <PageShell>
                    <div className="survey">
                        {previewStrip}
                        <div className="survey__confirm">
                            <h1 className="survey__title">{t.playtime.q12.thankYou(lang)}</h1>
                            {campaign.outro_description && (
                                <div
                                    className="survey__description survey__description--body"
                                    dangerouslySetInnerHTML={{
                                        __html: campaign.outro_description,
                                    }}
                                />
                            )}
                            <Button
                                text={t.playtime.q12.backToHome(lang)}
                                variant="primary"
                                onClick={() => {
                                    navigateTo('/portal')
                                }}
                            />
                        </div>
                    </div>
                </PageShell>
            </ViewBase>
        )
    }

    // Likert legend labels — use custom scale_labels if defined, fallback to i18n
    const scaleLabels = campaign.scale_labels
    const defaultLabels = [
        t.playtime.q12.likert.stronglyDisagree(lang),
        t.playtime.q12.likert.disagree(lang),
        t.playtime.q12.likert.neutral(lang),
        t.playtime.q12.likert.agree(lang),
        t.playtime.q12.likert.stronglyAgree(lang),
    ]
    const likertLegend = [1, 2, 3, 4, 5].map((value, index) => {
        const custom = scaleLabels?.[String(value)]
        const label = custom?.[lang] || defaultLabels[index]
        return { value, label }
    })

    // Questions step — handles both one_at_a_time and all_at_once display modes
    const isAllAtOnce = (previewDisplayMode ?? campaign.display_mode) === 'all_at_once'

    const isDotClickable = (targetIndex: number) => {
        if (targetIndex <= currentQuestionIndex) return true
        for (let j = currentQuestionIndex; j < targetIndex; j++) {
            const q = questions[j]
            const ans = answers[String(q.id)]
            if (q.is_required && (!ans || ans.trim() === '')) {
                return false
            }
        }
        return true
    }

    const canProceedFromCurrent = currentQuestion 
        ? (!currentQuestion.is_required || !!(answers[String(currentQuestion.id)]?.trim()))
        : true

    return (
        <ViewBase {...props.base}>
            <PageShell>
                <PageHeader title={campaign.name} />
                <div className="survey">
                    {previewStrip}
                    {/* Likert scale legend */}
                <div className="survey__legend">
                    {likertLegend.map((item) => (
                        <span key={item.value} className="survey__legend-item">
                            <strong>{item.value}</strong> = {item.label}
                        </span>
                    ))}
                </div>

                <div className="survey__progress">
                    <span className="survey__progress-answered">
                        {t.playtime.q12.answered(answeredCount, totalQuestions)(lang)}
                    </span>
                    {!isAllAtOnce && (
                        <span className="survey__progress-current">
                            {t.playtime.q12.questionOf(
                                currentQuestionIndex + 1,
                                totalQuestions,
                            )(lang)}
                        </span>
                    )}
                    {languageToggle}
                </div>

                {isAllAtOnce ? (
                    /* All at once — render all questions vertically */
                    <>
                        <div className="survey__questions-all">
                            {questions.map((q) => (
                                <SurveyQuestion
                                    key={q.id}
                                    question={q}
                                    value={answers[String(q.id)] ?? ''}
                                    onChange={(value) => handleAnswer(q.id, value)}
                                    lang={lang}
                                    scaleLabels={scaleLabels}
                                />
                            ))}
                        </div>
                        <div className="survey__nav">
                            <Button
                                text={
                                    isSubmitting
                                        ? t.playtime.q12.submitting(lang)
                                        : t.playtime.q12.submit(lang)
                                }
                                variant="primary"
                                isDisabled={!allAnswered || isSubmitting}
                                onClick={() => void handleSubmit()}
                            />
                        </div>
                    </>
                ) : (
                    /* One at a time — existing behavior */
                    <>
                        <div className="survey__question-single">
                            {currentQuestion && (
                                <SurveyQuestion
                                    key={currentQuestion.id}
                                    question={currentQuestion}
                                    value={answers[String(currentQuestion.id)] ?? ''}
                                    onChange={(value) => handleAnswer(currentQuestion.id, value)}
                                    lang={lang}
                                    scaleLabels={scaleLabels}
                                />
                            )}
                        </div>
                        <div className="survey__nav">
                            <Button
                                iconLeft={ChevronLeft}
                                text={t.playtime.q12.previous(lang)}
                                variant="secondary"
                                isDisabled={currentQuestionIndex === 0}
                                onClick={handlePrevQuestion}
                            />
                            <div className="survey__nav-dots">
                                {questions.map((q, i) => (
                                    <button
                                        key={q.id}
                                        type="button"
                                        disabled={!isDotClickable(i)}
                                        className={`survey__nav-dot ${
                                            i === currentQuestionIndex
                                                ? 'survey__nav-dot--active'
                                                : ''
                                        } ${
                                            answers[String(q.id)] &&
                                            answers[String(q.id)].trim() !== ''
                                                ? 'survey__nav-dot--answered'
                                                : ''
                                        }`}
                                        onClick={() => setCurrentQuestionIndex(i)}
                                        title={t.playtime.q12.questionNumber(i + 1)(lang)}
                                    />
                                ))}
                            </div>
                            {currentQuestionIndex < totalQuestions - 1 ? (
                                <Button
                                    iconRight={ChevronRight}
                                    text={t.playtime.q12.next(lang)}
                                    variant="secondary"
                                    isDisabled={!canProceedFromCurrent}
                                    onClick={handleNextQuestion}
                                />
                            ) : (
                                <Button
                                    text={
                                        isSubmitting
                                            ? t.playtime.q12.submitting(lang)
                                            : t.playtime.q12.submit(lang)
                                    }
                                    variant="primary"
                                    isDisabled={!allAnswered || isSubmitting}
                                    onClick={() => void handleSubmit()}
                                />
                            )}
                        </div>
                    </>
                )}
                </div>
            </PageShell>
        </ViewBase>
    )
}
